import axios from 'axios';
import CdnMixin from '@SHARED/js/Vue/mixins/CdnMixin';
import template from './MediaGalleryModule.html';
import getIconFromExtension from '../../filters/getIconFromExtension';
import DocumentMixin from '../../mixins/DocumentMixin';
import ImageMixin from '../../mixins/ImageMixin';

export default {
  // name: 'MediaGalleryModule',

  template,

  mixins: [DocumentMixin, ImageMixin, CdnMixin],

  props: {
    usedWithGallery: {
      type: Boolean,
      default: false,
    },
    relatedId: {
      type: Number,
      required: true,
    },
    relatedModel: {
      type: String,
      required: true,
    },
    totalSimultaneousUploads: {
      type: Number,
      default: 4,
    },
    maxFilesToUpload: {
      type: Number,
      default: 4,
    },
    galleryFiles: {
      type: Array,
      default() {
        return [];
      },
    },
    multipleChoice: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Number,
      default: 20,
    },
    fileTypes: {
      type: Array,
      default() {
        return ['file', 'image'];
      },
    },
    multipleMediaSelect: {
      type: Boolean,
      required: true,
    },
    formId: {
      type: String,
      default: 'gallery-addon',
    },
    galleryId: {
      type: String,
      default: 'media-modal',
    },
    fileInputName: {
      type: String,
      default: 'mediaFiles',
    },
    useWithFroala: {
      type: Boolean,
      default: false,
    },
    mediaRole: {
      type: String,
      default: 'featured',
    },
    overrideState: {
      type: String,
      default: 'vue',
    },
  },

  data() {
    return {
      state: this.overrideState,

      editCurrentMedia: {},

      // User selected media
      selectedMedia: {
        id: 0,
        path: null,
        file_type: '',
      },

      // Parameters
      params: {
        image: {
          title: '',
          description: '',
        },
        document: {
          title: '',
          description: '',
        },
      },

      // Pagination
      imagePaginationOptions: {
        from: 1,
        last_page: 1,
        per_page: 0,
        to: 0,
        total: 0,
        current_page: 1,
      },

      documentPaginationOptions: {
        from: 1,
        last_page: 1,
        per_page: 0,
        to: 0,
        total: 0,
        current_page: 1,
      },

      isFetchingImages: false,
      isFetchingDocuments: false,

      // The number of pages on both sides of the active page in the pagination
      offset: 2,

      // Visual parameters
      showUploadTab: true,
      showGalleryTab: false,
      showDocumentTab: false,
      showEditTab: false,
      showGallery: false,

      // User feedback
      mediaUpdateSuccess: '',
      mediaUpdateError: '',

      // Errors
      errors: {},
    };
  },

  filters: {
    getIconFromExtension,
  },

  computed: {
    hasErrors() {
      return !_.isEmpty(this.errors);
    },

    includeDocumentMedia() {
      return this.fileTypes.includes('document');
    },

    includeImageMedia() {
      return this.fileTypes.includes('image');
    },

    selectedImageFiles() {
      const mediaSelected = [];

      this.images.data.forEach((image) => {
        if (image.selected) {
          mediaSelected.push({
            id: image.id,
            file_type: image.file_type,
            path: image.path,
          });
        }
      });

      return mediaSelected;
    },

    selectedDocumentFiles() {
      const mediaSelected = [];

      this.documents.data.forEach((document) => {
        if (document.selected) {
          mediaSelected.push({
            id: document.id,
            file_type: document.file_type,
            path: document.path,
          });
        }
      });

      return mediaSelected;
    },

    selectedFiles() {
      return this.selectedImageFiles.concat(this.selectedDocumentFiles);
    },

    mediaPages() {
      if (!this.imagePaginationOptions.to) {
        return [];
      }

      let from = this.imagePaginationOptions.current_page - this.offset;
      let to = from + (this.offset * 2);
      const pagesArray = [];

      if (from < 1) {
        from = 1;
      }

      if (to >= this.imagePaginationOptions.last_page) {
        to = this.imagePaginationOptions.last_page;
      }

      while (from <= to) {
        pagesArray.push(from);
        from++;
      }

      return pagesArray;
    },

    documentPages() {
      if (!this.documentPaginationOptions.to) {
        return [];
      }

      let from = this.documentPaginationOptions.current_page - this.offset;
      let to = from + (this.offset * 2);
      const pagesArray = [];

      if (from < 1) {
        from = 1;
      }

      if (to >= this.documentPaginationOptions.last_page) {
        to = this.documentPaginationOptions.last_page;
      }

      while (from <= to) {
        pagesArray.push(from);
        from++;
      }

      return pagesArray;
    },
  },

  watch: {
    showUploadTab(currentValue) {
      if (currentValue) {
        this.showGalleryTab = false;
        this.showDocumentTab = false;
        this.showEditTab = false;

        this.mediaUpdateSuccess = '';
        this.mediaUpdateError = '';
      }
    },
    showGalleryTab(currentValue) {
      if (currentValue) {
        this.showUploadTab = false;
        this.showDocumentTab = false;
        this.showEditTab = false;

        this.mediaUpdateSuccess = '';
        this.mediaUpdateError = '';
      }
    },
    showDocumentTab(currentValue) {
      if (currentValue) {
        this.showUploadTab = false;
        this.showGalleryTab = false;
        this.showEditTab = false;

        this.mediaUpdateSuccess = '';
        this.mediaUpdateError = '';
      }
    },
    showEditTab(currentValue) {
      if (currentValue) {
        this.showGalleryTab = false;
        this.showDocumentTab = false;
        this.showUploadTab = false;
      }
    },
    'imagePaginationOptions.current_page': function () {
      this.imagePaginate();
    },
    'documentPaginationOptions.current_page': function () {
      this.documentPaginate();
    },
  },

  created() {
    this.getGalleryImages();
    this.getGalleryDocuments();
  },

  methods: {
    /*
    * Get all the images and documents on created
    */
    getGalleryImages() {
      this.isFetchingImages = true;
      this.resetData();

      axios.get(`/admin/api/v1/media/images?pagination=${this.pagination}&file_type=${this.fileTypes}`)
        .then((response) => {
          this.populateWithImages(response.data.data);

          this.imagePaginationOptions.from = response.data.from;
          this.imagePaginationOptions.last_page = response.data.last_page;
          this.imagePaginationOptions.per_page = response.data.per_page;
          this.imagePaginationOptions.to = response.data.to;
          this.imagePaginationOptions.total = response.data.total;
          this.imagePaginationOptions.current_page = response.data.current_page;
        })
        .catch((error) => {
          this.errors = error.response.data;
        })
        .finally(() => {
          this.isFetchingImages = false;
        });
    },

    getGalleryDocuments() {
      this.isFetchingDocuments = true;
      this.resetData();

      axios.get(`/admin/api/v1/media/documents?pagination=${this.pagination}&file_type=${this.fileTypes}`)
        .then((response) => {
          this.addDocumentsOnInit(response.data.data);

          this.documentPaginationOptions.from = response.data.from;
          this.documentPaginationOptions.last_page = response.data.last_page;
          this.documentPaginationOptions.per_page = response.data.per_page;
          this.documentPaginationOptions.to = response.data.to;
          this.documentPaginationOptions.total = response.data.total;
          this.documentPaginationOptions.current_page = response.data.current_page;
        })
        .catch((error) => {
          this.errors = error.response.data;
        })
        .finally(() => {
          this.isFetchingDocuments = false;
        });
    },

    /*
    * Init and froala editor
    */
    initOnForm() {
      this.showGallery = !this.showGallery;

      $(`#${this.galleryId}`).modal('show');
      $(`#${this.galleryId}`).one('hidden.bs.modal', () => this.destroyGallery());

      this.$emit('init-on-form');

      const initOnFormEvent = new CustomEvent(
        'init-on-form',
        {
          bubbles: true,
          cancelable: true,
        },
      );

      window.dispatchEvent(initOnFormEvent);
    },

    initOnFroalaEditor() {
      this.state = 'froala';
      this.showGallery = !this.showGallery;

      this.images.data.forEach(media => media.selected = false);
      this.documents.data.forEach(media => media.selected = false);

      $(`#${this.galleryId}`).modal('show');
      $(`#${this.galleryId}`).one('hidden.bs.modal', () => this.destroyGallery());

      this.$emit('init-on-froala-editor');

      const initOnFroalaEditorEvent = new CustomEvent(
        'init-on-froala-editor',
        {
          bubbles: true,
          cancelable: true,
        },
      );

      window.dispatchEvent(initOnFroalaEditorEvent);
    },

    destroyGallery() {
      this.state = 'vue';
      this.showGallery = !this.showGallery;

      $(`#${this.galleryId}`).modal('hide');

      this.$emit('destroy-gallery');

      const destroyGalleryEvent = new CustomEvent(
        'destroy-gallery',
        {
          bubbles: true,
          cancelable: true,
        },
      );

      // Dispatch the event when destroyed
      window.dispatchEvent(destroyGalleryEvent);
    },

    /*
    * Axios requests
    */
    editMedia(file) {
      this.resetData();
      this.showEditTab = true;

      if (file.file_type === 'image') {
        this.editCurrentMedia = this.getImageMediaFile(file.id);

        this.params.image.title = this.editCurrentMedia.title;
        this.params.image.description = this.editCurrentMedia.description;
      } else {
        this.editCurrentMedia = this.getDocumentMediaFile(file.id);

        this.params.document.title = this.editCurrentMedia.title;
        this.params.document.description = this.editCurrentMedia.description;
      }
    },

    updateMedia(file) {
      this.errors = {};
      let data = {};

      if (file.file_type === 'image') {
        data = this.params.image;
      } else {
        data = this.params.document;
      }

      axios.patch(`/admin/api/v1/media/update/${file.id}`, data)
        .then((response) => {
          this.mediaUpdateSuccess = response.data.message;
          this.editCurrentMedia = response.data.media;

          if (file.file_type === 'image') {
            this.updateImageMedia(file.id, this.params.image);
          } else {
            this.updateDocumentMedia(file.id, this.params.document);
          }

          this.$emit('update-media', file);

          const updateMediaEvent = new CustomEvent(
            'update-media',
            {
              detail: { file },
              bubbles: true,
              cancelable: true,
            },
          );

          // Dispatch the event with the updated file
          window.dispatchEvent(updateMediaEvent);
        })
        .catch((error) => {
          this.mediaUpdateError = error.response.data;
        });
    },

    deleteMedia(media) {
      this.errors = {};
      const self = this;

      swal({
        title: 'Er du sikker på du ønsker å slette?',
        text: 'Du kan ikke angre på denne handlingen. Dersom filen har tilknytning til andre sider eller lignende, vil disse også bli fjernet.',
        showCancelButton: true,
        closeOnConfirm: false,
      }, function () {
        axios.delete(`/admin/api/v1/media/destroy?media_id=${media.id}&entity_id=${self.relatedId}&entity_type=${self.relatedModel}`)
          .then((response) => {
            if (media.file_type === 'image') {
              self.deleteImageMedia(media.id);
            } else {
              self.deleteDocumentMedia(media.id);
            }

            self.showUploadTab = true;

            swal('', 'Media ble slettet.', 'success');

            this.$emit('delete-media', media);

            const deleteMediaEvent = new CustomEvent(
              'delete-media',
              {
                detail: { media },
                bubbles: true,
                cancelable: true,
              },
            );

            // Dispatch the event with the deleted media
            window.dispatchEvent(deleteMediaEvent);
          })
          .catch((error) => {
            swal('', 'Det oppstod en feil ved sletting av media.', 'error');
          });
      });
    },

    /*
    * Inject and mutate methods
    */
    resolveMediaInsert() {
      if (this.state === 'froala' || this.useWithFroala || this.state === 'standalone') {
        this.insertMediaToFroala();
      } else {
        this.appendMediaToRequest();
      }
    },

    insertMediaToFroala() {
      let selectedMedia = [];
      let mediaLength = null;

      if (this.multipleMediaSelect) {
        // If the array contains 2 elements, you know that user wants images and documents
        selectedMedia = this.selectedFiles;
        mediaLength = this.selectedFiles.length;

        // Set all media to deselected
        this.images.data.forEach(media => media.selected = false);
        this.documents.data.forEach(media => media.selected = false);
      } else {
        selectedMedia.push(this.selectedMedia);
        mediaLength = selectedMedia.length;
      }

      this.$emit('insert-gallery', {
        media: selectedMedia,
        mediaItems: mediaLength,
      });

      const insertGalleryEvent = new CustomEvent(
        'insertGallery',
        {
          detail: {
            media: selectedMedia,
            mediaItems: mediaLength,
          },
          bubbles: true,
          cancelable: true,
        },
      );

      // Dispatch the event with the selected files
      window.dispatchEvent(insertGalleryEvent);

      // Close the gallery
      this.showGallery = !this.showGallery;

      $(`#${this.galleryId}`).modal('hide');
    },

    appendMediaToRequest() {
      const form = document.getElementById(this.formId);
      const existingInput = document.getElementById('unique-input-id-for-gallery-component');
      const input = document.createElement('input');
      const mediaRoleInput = document.createElement('input');
      const selectedMedia = [];

      // If the form is not found, don't allow any further actions and close the gallery
      if (!form) {
        alert(`Could not find form with id="${this.formId}"`);

        this.destroyGallery();

        return;
      }

      if (this.multipleMediaSelect) {
        this.selectedFiles.forEach(media => selectedMedia.push(media.id));
      } else {
        selectedMedia.push(this.selectedMedia.id);
      }

      if (existingInput) {
        existingInput.value = JSON.stringify(selectedMedia);

        this.renderSelectedMedia(selectedMedia, form);
      } else {
        input.name = this.fileInputName;
        input.value = JSON.stringify(selectedMedia);
        input.id = 'unique-input-id-for-gallery-component';
        input.type = 'hidden';

        form.appendChild(input);

        mediaRoleInput.name = 'mediaRole';
        mediaRoleInput.value = this.mediaRole;
        mediaRoleInput.id = 'unique-input-id-for-gallery-media-role';
        mediaRoleInput.type = 'hidden';

        form.appendChild(mediaRoleInput);

        $(`#${this.formId}`).trigger('submit');
      }

      this.showGallery = !this.showGallery;

      $(`#${this.galleryId}`).modal('hide');

      this.$emit('append-media-to-request');

      const appendMediaToRequestEvent = new CustomEvent(
        'appendMediaToRequest',
        {
          bubbles: true,
          cancelable: true,
        },
      );

      window.dispatchEvent(appendMediaToRequestEvent);
    },

    renderSelectedMedia(mediaFiles, form) {
      const imagePreviewDiv = document.getElementById('preview-image-gallery-form');
      const divElement = document.createElement('div');

      divElement.setAttribute('id', 'preview-image-gallery-form');

      // If it exists, delete it and create the new image previews
      if (imagePreviewDiv) {
        imagePreviewDiv.remove();
      }

      this.selectedFiles.forEach((media) => {
        if (media.file_type === 'image') {
          const image = document.createElement('img');

          image.src = media.small;
          image.className = 'gallery-preview-form img-fluid img-thumbnail';

          // Inject the images in the html as preview
          divElement.append(image);
          form.append(divElement);
        } else {
          const fileType = document.createElement('i');

          fileType.className = `gallery-preview-form img-fluid img-thumbnail fa-5x fa ${getIconFromExtension(media.path)}`;

          divElement.append(fileType);
          form.append(divElement);
        }
      });

      this.images.data.forEach(media => media.selected = false);
      this.documents.data.forEach(media => media.selected = false);
    },

    selectFile(file) {
      if (this.multipleMediaSelect) {
        file.selected = !file.selected;
      } else {
        this.resetSelectedMedia();

        // Reset selected status on images and documents before selecting the new file/image
        this.images.data.forEach(media => media.selected = false);
        this.documents.data.forEach(media => media.selected = false);

        // Set selected on file chosen
        file.selected = true;

        this.selectedMedia.id = file.id;
        this.selectedMedia.path = file.path;
        this.selectedMedia.file_type = file.file_type;
      }
    },

    /*
    * Pagination
    */
    imagePaginate() {
      axios.get(`/admin/api/v1/media/images?page=${this.imagePaginationOptions.current_page}&pagination=${this.pagination}`)
        .then((response) => {
          this.images.data = [];
          this.populateWithImages(response.data.data);
        })
        .catch((error) => {
          swal('', 'Det oppstod en feil ved uthenting av bilder', 'error');
        });
    },

    documentPaginate() {
      axios.get(`/admin/api/v1/media/documents?page=${this.documentPaginationOptions.current_page}&pagination=${this.pagination}`)
        .then((response) => {
          this.documents.data = [];
          this.populateWithDocuments(response.data.data);
        })
        .catch((error) => {
          swal('', 'Det oppstod en feil ved uthenting av bilder', 'error');
        });
    },

    /*
    * TODO Replace with CSS truncate
    * Helper methods
    */
    limitTitleLength(title) {
      if (title !== null && title.length > 12) {
        return `${title.substr(0, 12)}..`;
      }

      return title;
    },

    unselectAllMedia() {
      this.images.data.forEach(media => media.selected = false);
      this.documents.data.forEach(media => media.selected = false);
    },

    resetData() {
      this.editCurrentMedia = {};
      this.errors = {};
      this.params.image.title = '';
      this.params.image.description = '';
      this.params.document.title = '';
      this.params.document.description = '';
    },

    resetSelectedMedia() {
      this.selectedMedia.path = 0;
      this.selectedMedia.path = null;
      this.selectedMedia.file_type = '';
    },
  },
};
